/**
 * Runtime configuration. One built image serves every environment: nginx drops a `/config.json`
 * next to the bundle (see sf.deployment/web.dockerfile) and the SPA reads it at boot.
 * In dev there is no config.json — Vite proxies `/api` to the local API, so the fallback wins.
 */
export interface RuntimeConfig {
  /** Absolute base URL of the API, or '' to use the current origin (dev proxy / same-host ingress). */
  apiBaseUrl: string;
  /** Tenant slug sent as the X-Tenant header. */
  tenant: string;
  /** Optional: overrides the app title / support address without a rebuild. */
  appName?: string;
  supportEmail?: string;
  environment?: string;
}

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  apiBaseUrl: '',
  // No baked brand: an empty slug means "let the server resolve the tenant from the hostname" (ADR-0007).
  tenant: '',
};

/**
 * Loads `/config.json`. Never throws: a missing or malformed file falls back to the defaults so the
 * app still boots (and, in dev, still works through the Vite proxy).
 */
export async function loadRuntimeConfig(url = '/config.json'): Promise<RuntimeConfig> {
  try {
    const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) return { ...DEFAULT_RUNTIME_CONFIG };
    const data = (await res.json()) as Partial<RuntimeConfig>;
    return {
      ...DEFAULT_RUNTIME_CONFIG,
      ...data,
      apiBaseUrl: (data.apiBaseUrl ?? DEFAULT_RUNTIME_CONFIG.apiBaseUrl).replace(/\/+$/, ''),
      tenant: data.tenant || DEFAULT_RUNTIME_CONFIG.tenant,
    };
  } catch {
    return { ...DEFAULT_RUNTIME_CONFIG };
  }
}
